import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@volca/ui/components/ui/dialog";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { useStepDialog } from "./use-step-dialog";

interface SubmissionConfirmModalProps {
  open: boolean;
  isSaving: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

// Saying "I have sent everything" puts a person to work on it, so it is worth one
// deliberate step. The modal is also the honest place for the caveat: complete enough is
// our judgement, not theirs, and we may come back for more.
export const SubmissionConfirmModal = ({
  open,
  isSaving,
  error,
  onCancel,
  onConfirm,
}: SubmissionConfirmModalProps) => {
  const dialogOpen = useStepDialog(open, onCancel);
  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(next) => {
        // Dismissing mid-save would hide the outcome, so hold the modal open.
        if (!next && !isSaving) onCancel();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Send everything for review?</DialogTitle>
        </DialogHeader>
        <DialogDescription className="sr-only">
          Send your documents for review. Volca builds a first version of your
          payroll from them.
        </DialogDescription>

        <Stack className="w-full" gap={4} align="stretch">
          <Text tone="muted">
            We will build a first version of your payroll from what you sent.
            That usually takes a few days.
          </Text>
          <Text tone="muted">
            If something is missing, or we cannot follow how a number was worked
            out, we will come back and ask you for more. That is normal, and it
            is quicker than us guessing.
          </Text>
          <Text variant="caption" tone="muted">
            If you find more later, you can reopen these steps and send it.
          </Text>
          {error && <Alert color="danger">{error}</Alert>}
        </Stack>

        <DialogFooter>
          <Button
            variant="secondary"
            type="button"
            disabled={isSaving}
            onClick={onCancel}
          >
            Not yet
          </Button>
          <Button
            type="button"
            disabled={isSaving}
            loading={isSaving}
            onClick={onConfirm}
          >
            Send for review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
